/**
 * DemandMap — bird's-eye view of all cluster pins.
 *
 * Uses @vis.gl/react-google-maps for the Google Maps JS API and
 * @googlemaps/markerclusterer for clustering dense pin regions at low zoom.
 *
 * This component is a pure consumer of a `clusters` prop — it does NOT fetch
 * data itself. The parent (ChurchDemandLanding) owns data fetching and passes
 * cluster rows in via props.
 *
 * Click-to-detail: parent passes an optional `onPinClick(cluster)` handler;
 * the pin invokes it on click. If unset, pins render but don't navigate.
 */

import { useEffect, useRef, useState } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  useMap,
} from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

// Denomination → background color. Unknown / null falls back to light gray.
// Colors are deliberately saturated so they read clearly at small pin sizes.
const DENOMINATION_COLORS = {
  catholic: '#7e22ce',    // purple-700
  baptist: '#dc2626',     // red-600
  methodist: '#2563eb',   // blue-600
  presbyterian: '#0891b2', // cyan-600
  lutheran: '#ea580c',    // orange-600
  pentecostal: '#db2777', // pink-600
  evangelical: '#16a34a', // green-600
  'non-denominational': '#6b7280', // gray-500
};
const DEFAULT_COLOR = '#9ca3af'; // gray-400 — unknown / null denomination

// Trust level → border color. Human-reviewed and high-confidence auto tiers
// get a solid black border that reads strongly against any map background;
// lower-confidence tiers get a dim gray border so they're still visible but
// subtly de-emphasized. (The previous opacity-based encoding made auto_low
// pins invisible on busy basemaps — see visibility feedback.)
const TRUST_BORDER = {
  auto_high: '#000000',
  auto_medium: '#1f2937',      // gray-800
  auto_low: '#6b7280',         // gray-500
  human_confirmed: '#000000',
  human_overridden: '#000000',
};
const DEFAULT_BORDER = '#4b5563'; // gray-600

// Centroid-fallback coord sources — marked with a ~ glyph to indicate approximate
const CENTROID_SOURCES = new Set(['state_centroid', 'city_centroid']);

// Center of the contiguous US — sensible default for an all-US pin view
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const DEFAULT_ZOOM = 4;

// Map container height — fixed so the clustering math has a stable viewport
const MAP_HEIGHT_PX = 520;

/**
 * Inner component that renders markers + wires markerclusterer.
 * Must live inside <Map> so it can call useMap().
 */
function ClusteredMarkers({ clusters, onPinClick }) {
  const map = useMap();
  const [markerMap, setMarkerMap] = useState({});
  const clustererRef = useRef(null);
  // Per-id ref callback cache. Callback refs on <AdvancedMarker> MUST keep
  // stable identity across renders — otherwise React detaches the old ref
  // (null) and attaches the new one on every render, each triggering a
  // setMarkerMap, which re-renders, which rebuilds the ref, infinite loop.
  // Plain object (not JS Map) because `Map` is shadowed by the react-google-maps
  // component import at the top of this file; cluster ids are string UUIDs so
  // a plain object works identically for get/set/has here.
  const refCacheRef = useRef({});

  // Instantiate the clusterer once per <Map> lifetime
  useEffect(() => {
    if (!map) return;
    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map });
    }
  }, [map]);

  // Sync the clusterer's markers whenever the marker map changes
  useEffect(() => {
    const clusterer = clustererRef.current;
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markerMap));
  }, [markerMap]);

  // Tear down on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    };
  }, []);

  // Returns a stable callback ref for a given cluster id. First call creates
  // and caches the callback; subsequent calls return the same function so
  // React doesn't treat it as a new ref. The callback itself uses the
  // functional updater form of setMarkerMap, so it never captures stale state.
  const getMarkerRef = (id) => {
    let fn = refCacheRef.current[id];
    if (!fn) {
      fn = (marker) => {
        setMarkerMap((prev) => {
          if (marker && prev[id] === marker) return prev;
          if (!marker && !prev[id]) return prev;
          const next = { ...prev };
          if (marker) {
            next[id] = marker;
          } else {
            delete next[id];
          }
          return next;
        });
      };
      refCacheRef.current[id] = fn;
    }
    return fn;
  };

  return (
    <>
      {clusters.map((cluster) => {
        if (
          cluster == null ||
          typeof cluster.lat !== 'number' ||
          typeof cluster.lng !== 'number' ||
          Number.isNaN(cluster.lat) ||
          Number.isNaN(cluster.lng)
        ) {
          return null;
        }
        // Lowercase the denomination for lookup since the backend can return
        // either "Baptist" or "baptist" depending on source (Haiku output vs
        // normalized values).
        const denomKey =
          typeof cluster.denomination === 'string'
            ? cluster.denomination.toLowerCase()
            : null;
        const bg = DENOMINATION_COLORS[denomKey] || DEFAULT_COLOR;
        const border = TRUST_BORDER[cluster.trust_level] || DEFAULT_BORDER;
        const isCentroid = CENTROID_SOURCES.has(cluster.coord_source);
        return (
          <AdvancedMarker
            key={cluster.id}
            position={{ lat: cluster.lat, lng: cluster.lng }}
            ref={getMarkerRef(cluster.id)}
            onClick={onPinClick ? () => onPinClick(cluster) : undefined}
          >
            <Pin
              background={bg}
              borderColor={border}
              glyphColor={'#ffffff'}
              scale={1.2}
            >
              {isCentroid ? '~' : null}
            </Pin>
          </AdvancedMarker>
        );
      })}
    </>
  );
}

export default function DemandMap({ clusters = [], onPinClick }) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_JS_API_KEY;

  if (!apiKey) {
    return (
      <div
        className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-6 text-sm"
        style={{ height: MAP_HEIGHT_PX }}
      >
        <strong className="font-semibold">Map unavailable:</strong>{' '}
        <code>VITE_GOOGLE_MAPS_JS_API_KEY</code> is not set in{' '}
        <code>frontend/test-harness/.env</code>.
      </div>
    );
  }

  if (!clusters || clusters.length === 0) {
    return (
      <div
        className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-sm text-gray-600 flex items-center justify-center"
        style={{ height: MAP_HEIGHT_PX }}
      >
        No clusters to plot yet.
      </div>
    );
  }

  return (
    <div
      className="rounded-xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height: MAP_HEIGHT_PX, width: '100%' }}
    >
      <APIProvider apiKey={apiKey}>
        <Map
          mapId="church-demand-map"
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="cooperative"
          disableDefaultUI={false}
        >
          <ClusteredMarkers clusters={clusters} onPinClick={onPinClick} />
        </Map>
      </APIProvider>
    </div>
  );
}
